import { Head, useForm, usePage } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { newStoryPath, storiesPath } from "@/routes"
import type {
  BreadcrumbItem,
  MentionHandleGroup,
  Platform,
  PlatformCredentials,
  SharedProps,
  YoutubeCategory,
} from "@/types"

import LongFormStoryForm from "./long-form-story-form"
import StoryForm, { type StoryFormData } from "./story-form"
import { emptyTakeAttributes } from "./story-form-utils"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Newsroom", href: storiesPath() },
  { title: "Compose", href: newStoryPath() },
]

interface SharedComposeProps {
  available_platforms: Platform[]
  platform_credentials: PlatformCredentials
  mention_handle_groups: MentionHandleGroup[]
  mention_platforms: Platform[]
  mastodon_server_domain?: string | null
  youtube_categories: YoutubeCategory[]
  default_youtube_category_id: number
}

type Props = SharedComposeProps & {
  type?: string | null
}

function emptyStory(): StoryFormData {
  return {
    title: "",
    tags: [],
    scheduled_at: "",
    syndications_attributes: {},
    takes_attributes: {
      0: { ...emptyTakeAttributes(), position: 0 },
    },
  }
}

function ShortFormCompose({
  available_platforms,
  platform_credentials,
  mention_handle_groups,
  mention_platforms,
  mastodon_server_domain,
  youtube_categories,
  default_youtube_category_id,
}: SharedComposeProps) {
  const { story_types } = usePage<SharedProps>().props

  const { data, setData, post, processing, errors, transform } = useForm<{
    story: StoryFormData
  }>({ story: emptyStory() })

  function handleSubmit(e: React.FormEvent, value: StoryFormData) {
    e.preventDefault()
    transform(() => ({ story: { ...value, type: story_types.short_form } }))
    post(storiesPath(), {
      onFinish: () => transform((current) => current),
    })
  }

  return (
    <StoryForm
      data={data.story}
      errors={(errors.story as unknown as Record<string, string>) ?? {}}
      processing={processing}
      availablePlatforms={available_platforms}
      platformCredentials={platform_credentials}
      mentionHandleGroups={mention_handle_groups}
      mentionPlatforms={mention_platforms}
      mastodonServerDomain={mastodon_server_domain}
      youtubeCategories={youtube_categories}
      defaultYoutubeCategoryId={default_youtube_category_id}
      onChange={(value) => setData("story", value)}
      onSubmit={handleSubmit}
      onCancel={() => window.history.back()}
    />
  )
}

function LongFormCompose({
  available_platforms,
  platform_credentials,
  youtube_categories,
  default_youtube_category_id,
}: SharedComposeProps) {
  const { story_types } = usePage<SharedProps>().props

  const { data, setData, post, processing, errors, transform } = useForm<{
    story: StoryFormData
  }>({ story: emptyStory() })

  function handleSubmit(e: React.FormEvent, value: StoryFormData) {
    e.preventDefault()
    transform(() => ({ story: { ...value, type: story_types.long_form } }))
    post(storiesPath(), {
      onFinish: () => transform((current) => current),
    })
  }

  return (
    <LongFormStoryForm
      data={data.story}
      errors={(errors.story as unknown as Record<string, string>) ?? {}}
      processing={processing}
      availablePlatforms={available_platforms}
      platformCredentials={platform_credentials}
      youtubeCategories={youtube_categories}
      defaultYoutubeCategoryId={default_youtube_category_id}
      onChange={(value) => setData("story", value)}
      onSubmit={handleSubmit}
      onCancel={() => window.history.back()}
    />
  )
}

export default function StoriesNew({ type, ...shared }: Props) {
  const { story_types } = usePage<SharedProps>().props

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Compose" />

      <div className="flex flex-col gap-6 p-6">
        {type === story_types.long_form ? (
          <LongFormCompose {...shared} />
        ) : (
          <ShortFormCompose {...shared} />
        )}
      </div>
    </AppLayout>
  )
}
